import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { CKEditor } from '@ckeditor/ckeditor5-react';
import 'ckeditor5/ckeditor5.css';
import './TextEditor.css';
import './ckContent.css';
import { IMAGE_UPLOAD_URL } from '../../services/ImageServices';
import ImportWord from './plugins/importword/ImportWord';
import MathPlugin from './plugins/math/Math';
import { describeWarnings } from './plugins/importword/importReport';
import {
	ClassicEditor,
	Alignment,
	AutoImage,
	AutoLink,
	// Autosave, 
	BlockQuote,
	Bold,
	Bookmark,
	CloudServices,
	Code,
	CKFinderUploadAdapter,
	Essentials,
	FindAndReplace,
	FontBackgroundColor,
	FontColor,
	FontFamily,
	FontSize,
	GeneralHtmlSupport,
	Heading,
	Highlight,
	HorizontalLine,
	HtmlComment,
	HtmlEmbed,
	ImageBlock,
	ImageCaption,
	ImageInline,
	ImageInsert,
	ImageInsertViaUrl,
	ImageResize,
	ImageStyle,
	ImageTextAlternative,
	ImageToolbar,
	ImageUpload,
	Indent,
	IndentBlock,
	Italic,
	Link,
	LinkImage,
	List,
	ListProperties,
	// Markdown,
	MediaEmbed,
	Mention,
	PageBreak,
	Paragraph,
	PasteFromMarkdownExperimental,
	PasteFromOffice,
	RemoveFormat,
	SimpleUploadAdapter,
	SpecialCharacters,
	SpecialCharactersArrows,
	SpecialCharactersCurrency,
	SpecialCharactersEssentials,
	SpecialCharactersLatin,
	SpecialCharactersMathematical,
	SpecialCharactersText,
	Strikethrough,
	Style,
	Subscript,
	Superscript,
	Table,
	TableCaption,
	TableCellProperties,
	TableColumnResize,
	TableProperties,
	TableToolbar,
	TextPartLanguage,
	TodoList,
	Underline,
	WordCount
} from 'ckeditor5';

const LICENSE_KEY = 'GPL';

/** Whoever turned read-only on. CKEditor keys its locks, so a named one can be lifted. */
const READ_ONLY_LOCK = 'external-readonly';

/** How many of its own recent outputs the editor remembers, to recognise echoes from the parent. */
const EMITTED_MEMORY = 5;

const IMPORT_MESSAGES = {
    converting: 'Converting Word document...',
    uploading: 'Uploading images...',
    inserting: 'Inserting content...',
};

const TextEditor = ({
    initialData = '',
    onChange: onChangeProp,
    onBlur: onBlurProp,
    editorRef,
    readOnly = false,
    enableWordImport = true,
    maxImportBytes,
}) => {
    const editorInstanceRef = useRef(null);
    const [isEditorReady, setIsEditorReady] = useState(false);
    // State to track if an upload is in progress
    const [isUploading, setIsUploading] = useState(false);
    // Word import progress and the report shown after it.
    const [importState, setImportState] = useState({ state: 'idle', progress: 0 });
    const [importReport, setImportReport] = useState(null);

    // Callbacks live in refs so editor listeners don't re-subscribe every render.
    const onChangeRef = useRef(onChangeProp);
    const onBlurRef = useRef(onBlurProp);
    onChangeRef.current = onChangeProp;
    onBlurRef.current = onBlurProp;

    // The React integration calls editor.data.set() whenever its `data` prop
    // changes and differs from the editor, which wipes undo (and an import) when
    // a parent echoes back an older value. So <CKEditor> only ever gets the
    // mount-time value, and later changes are applied below, deliberately.
    const mountDataRef = useRef(initialData);
    const emittedRef = useRef([initialData]);
    // The last value handed to onBlur, so unmounting only commits what is new.
    const lastBlurredRef = useRef(initialData);

    const emitBlur = (html) => {
        lastBlurredRef.current = html;
        if (onBlurRef.current) onBlurRef.current(html);
    };

    const editorConfig = useMemo(() => {
        return {
            toolbar: {
                items: [
                    ...(enableWordImport ? ['importWord', '|'] : []),
                    'findAndReplace', 'textPartLanguage', '|', 'heading', 'style', '|',
                    'fontSize', 'fontFamily', 'fontColor', 'fontBackgroundColor', '|',
                    'bold', 'italic', 'underline', 'strikethrough', 'subscript', 'superscript', 'code', 'removeFormat', '|',
                    'specialCharacters', 'math', 'horizontalLine', 'pageBreak', 'link', 'bookmark',
                    'insertImage', 'insertImageViaUrl', 'mediaEmbed', 'insertTable', 'highlight',
                    'blockQuote', 'htmlEmbed', '|', 'alignment', '|',
                    'bulletedList', 'numberedList', 'todoList', 'outdent', 'indent'
                ],
                shouldNotGroupWhenFull: true
            },
            plugins: [
                Alignment, AutoImage, AutoLink, /* Autosave, */ BlockQuote, Bold, Bookmark, CloudServices, Code,
                CKFinderUploadAdapter, Essentials, FindAndReplace, FontBackgroundColor, FontColor, FontFamily, FontSize,
                GeneralHtmlSupport, Heading, Highlight, HorizontalLine, HtmlComment, HtmlEmbed, ImageBlock, ImageCaption,
                ImageInline, ImageInsert, ImageInsertViaUrl, ImageResize, ImageStyle, ImageTextAlternative, ImageToolbar, ImageUpload,
                Indent, IndentBlock, Italic, Link, LinkImage, List, ListProperties, MediaEmbed, Mention, PageBreak,
                Paragraph, PasteFromMarkdownExperimental, PasteFromOffice, RemoveFormat, SimpleUploadAdapter,
                SpecialCharacters, SpecialCharactersArrows, SpecialCharactersCurrency, SpecialCharactersEssentials,
                SpecialCharactersLatin, SpecialCharactersMathematical, SpecialCharactersText, Strikethrough, Style,
                Subscript, Superscript, Table, TableCaption, TableCellProperties, TableColumnResize, TableProperties,
                TableToolbar, TextPartLanguage, TodoList, Underline, WordCount, ImportWord, MathPlugin
            ],
            licenseKey: LICENSE_KEY,
            importWord: maxImportBytes ? { maxImportBytes } : {},
            fontFamily: { supportAllValues: true },
            fontSize: {
                options: [5, 5.5, 6.5, 7.5, 8, 9, 10, 12, 14, 'default', 18, 20, 22, 24, 26, 28, 36, 48],
                supportAllValues: true
            },
            mediaEmbed: { elementName: 'iframe' },
            ckfinder: { uploadUrl: IMAGE_UPLOAD_URL },
            heading: {
                options: [
                    { model: 'paragraph', title: 'Paragraph', class: 'ck-heading_paragraph' },
                    { model: 'heading1', view: 'h1', title: 'Heading 1', class: 'ck-heading_heading1' },
                    { model: 'heading2', view: 'h2', title: 'Heading 2', class: 'ck-heading_heading2' },
                    { model: 'heading3', view: 'h3', title: 'Heading 3', class: 'ck-heading_heading3' },
                    { model: 'heading4', view: 'h4', title: 'Heading 4', class: 'ck-heading_heading4' },
                    { model: 'heading5', view: 'h5', title: 'Heading 5', class: 'ck-heading_heading5' },
                    { model: 'heading6', view: 'h6', title: 'Heading 6', class: 'ck-heading_heading6' }
                ]
            },
            htmlSupport: { allow: [{ name: /^.*$/, styles: true, attributes: true, classes: true }] },
            image: {
                toolbar: [
                    'toggleImageCaption', 'imageTextAlternative', '|',
                    'imageStyle:inline', 'imageStyle:wrapText', 'imageStyle:breakText', '|',
                    'resizeImage'
                ]
            },
            link: {
                addTargetToExternalLinks: true, defaultProtocol: 'https://',
                decorators: { toggleDownloadable: { mode: 'manual', label: 'Downloadable', attributes: { download: 'file' } } }
            },
            list: { properties: { styles: true, startIndex: true, reversed: true } },
            mention: { feeds: [{ marker: '@', feed: [] }] },
            style: {
                definitions: [
                    { name: 'Article category', element: 'h3', classes: ['category'] },
                    { name: 'Title', element: 'h2', classes: ['document-title'] },
                    { name: 'Subtitle', element: 'h3', classes: ['document-subtitle'] },
                    { name: 'Info box', element: 'p', classes: ['info-box'] },
                    { name: 'Side quote', element: 'blockquote', classes: ['side-quote'] },
                    { name: 'Marker', element: 'span', classes: ['marker'] },
                    { name: 'Spoiler', element: 'span', classes: ['spoiler'] },
                    { name: 'Code (dark)', element: 'pre', classes: ['fancy-code', 'fancy-code-dark'] },
                    { name: 'Code (bright)', element: 'pre', classes: ['fancy-code', 'fancy-code-bright'] }
                ]
            },
            table: { contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells', 'tableProperties', 'tableCellProperties'] }
        };
        // The editor is configured once; these props are fixed per instance.
    }, []);

    /**
     * Applies a genuinely new `initialData` (e.g. the parent switched to another
     * document). Values this editor emitted itself, even older ones arriving
     * late, are echoes and are ignored.
     */
    useEffect(() => {
        if (!isEditorReady) return;
        const editor = editorInstanceRef.current;
        if (!editor || editor.state === 'destroyed') return;
        if (emittedRef.current.includes(initialData)) return;
        if (initialData === editor.getData()) return;
        emittedRef.current = [initialData];
        editor.setData(initialData);
    }, [initialData, isEditorReady]);

    // Word import: progress overlay, report banner, and commit once it lands.
    useEffect(() => {
        if (!isEditorReady) return undefined;
        const editor = editorInstanceRef.current;
        if (!editor || editor.state === 'destroyed' || !editor.plugins.has('ImportWord')) return undefined;
        const importer = editor.plugins.get('ImportWord');

        const onState = () => setImportState({ state: importer.state, progress: importer.progress });
        const onDone = (evt, report) => {
            setImportReport({ type: 'success', lines: describeWarnings(report.warnings) });
            // Consumers commit on blur; an import must not depend on a later blur.
            if (editor.state !== 'destroyed') emitBlur(editor.getData());
        };
        const onError = (evt, error) => {
            if (error && error.code === 'ABORTED') return;
            setImportReport({ type: 'error', lines: [error ? error.message : 'The import failed.'] });
        };
        importer.on('change:state', onState);
        importer.on('change:progress', onState);
        importer.on('done', onDone);
        importer.on('error', onError);
        return () => {
            importer.off('change:state', onState);
            importer.off('change:progress', onState);
            importer.off('done', onDone);
            importer.off('error', onError);
        };
    }, [isEditorReady]);


    /**
     * Read-only is a lock rather than a flag so that turning it off here cannot
     * unlock an editor something else also locked. Setting it programmatically
     * still works — a guided tutorial pre-fills content the user may not edit.
     */
    useEffect(() => {
        if (!isEditorReady) return;
        const editor = editorInstanceRef.current;
        if (!editor || editor.state === 'destroyed') return;

        if (readOnly) editor.enableReadOnlyMode(READ_ONLY_LOCK);
        else editor.disableReadOnlyMode(READ_ONLY_LOCK);
    }, [readOnly, isEditorReady]);

    useEffect(() => {
        if (!isEditorReady) {
            return;
        }

        const editor = editorInstanceRef.current;
        if (!editor || editor.state === 'destroyed') {
            return;
        }

        const focusTracker = editor.ui.focusTracker;
        
        const handleFocusChange = () => {
            if (!focusTracker.isFocused) {
                setTimeout(() => {
                    const currentEditor = editorInstanceRef.current;
                    if (!currentEditor || currentEditor.state === 'destroyed') {
                        return;
                    }
                    if (!currentEditor.ui || !currentEditor.ui.view) {
                        console.warn('TextEditor: Editor UI or UI View not available for focus check.');
                        return;
                    }

                    const activeElement = document.activeElement;
                    const editorUIElement = currentEditor.ui.view.element;
                    
                    let isFocusStillInsideEditorComponent = false;

                    if (editorUIElement && editorUIElement.contains(activeElement)) {
                        isFocusStillInsideEditorComponent = true;
                    } else {
                        const bodyCollection = currentEditor.ui.view.bodyCollection;
                        if (bodyCollection) {
                            if (typeof bodyCollection[Symbol.iterator] === 'function') {
                                for (const panelView of bodyCollection) {
                                    if (panelView && panelView.element && panelView.element.contains(activeElement)) {
                                        isFocusStillInsideEditorComponent = true;
                                        break;
                                    }
                                }
                            } else {
                                console.warn(
                                    'TextEditor: CKEditor bodyCollection is defined but not iterable. Type:', 
                                    Object.prototype.toString.call(bodyCollection), 
                                    'Value:', bodyCollection
                                );
                            }
                        }
                    }
                    
                    if (!isFocusStillInsideEditorComponent) {
                        if (currentEditor && currentEditor.state !== 'destroyed') {
                            emitBlur(currentEditor.getData());
                        }
                    }
                }, 0);
            }
        };

        focusTracker.on('change:isFocused', handleFocusChange);

        return () => {
            if (editor && editor.ui && editor.ui.focusTracker && editor.state !== 'destroyed') {
                 editor.ui.focusTracker.off('change:isFocused', handleFocusChange);
            }
        };
    }, [isEditorReady]);

    /**
     * Commits anything not yet blurred when the editor goes away — the parent
     * remounts it whenever the selection moves, which can beat the deferred blur
     * above. A layout cleanup runs before <CKEditor> starts destroying the
     * editor; if it is already gone, the last emitted value stands in for it.
     * onBlurRef still holds the unmounting parent's handler, so the commit lands
     * on the item that was being edited.
     */
    useLayoutEffect(() => () => {
        const editor = editorInstanceRef.current;
        const html = editor && editor.state !== 'destroyed' ? editor.getData() : emittedRef.current[0];
        if (html !== lastBlurredRef.current) emitBlur(html);
    }, []);

    const isImporting = importState.state !== 'idle';
    let reportTitle = 'Word document imported.';
    if (importReport && importReport.type === 'error') reportTitle = 'The Word document could not be imported.';
    else if (importReport && importReport.lines.length) reportTitle = 'Word document imported. Some content was not imported:';

    return (
        <>
            {isImporting && (
                <div className="text-editor-upload-overlay" role="status" aria-live="polite">
                    <div className="text-editor-spinner"></div>
                    <div className="text-editor-upload-message">
                        {IMPORT_MESSAGES[importState.state]} {Math.round(importState.progress * 100)}%
                    </div>
                    <button
                        type="button"
                        className="text-editor-import-cancel"
                        onClick={() => {
                            const editor = editorInstanceRef.current;
                            if (editor && editor.plugins.has('ImportWord')) editor.plugins.get('ImportWord').cancel();
                        }}
                    >
                        Cancel
                    </button>
                </div>
            )}
            {isUploading && !isImporting && (
                <div className="text-editor-upload-overlay">
                    <div className="text-editor-spinner"></div>
                    <div className="text-editor-upload-message">Uploading Image... Please Wait</div>
                </div>
            )}
            {importReport && (
                <div
                    className={`text-editor-import-report text-editor-import-report--${importReport.type}`}
                    role={importReport.type === 'error' ? 'alert' : 'status'}
                >
                    <div className="text-editor-import-report__body">
                        <strong>{reportTitle}</strong>
                        {importReport.lines.length > 0 && importReport.type !== 'error' && (
                            <ul>
                                {importReport.lines.map((line) => <li key={line}>{line}</li>)}
                            </ul>
                        )}
                        {importReport.type === 'error' && <div>{importReport.lines[0]}</div>}
                    </div>
                    <button
                        type="button"
                        className="text-editor-import-report__close"
                        aria-label="Dismiss"
                        onClick={() => setImportReport(null)}
                    >
                        &times;
                    </button>
                </div>
            )}

            <CKEditor
                editor={ClassicEditor}
                config={editorConfig}
                data={mountDataRef.current}
                onReady={editor => {
                    editorInstanceRef.current = editor;
                    if (editorRef) editorRef.current = editor;
                    
                    const fileRepository = editor.plugins.get('FileRepository');
                    const loaders = fileRepository.loaders;

                    const checkPendingUploads = () => {
                        setIsUploading(loaders.length > 0);
                    };

                    loaders.on('add', checkPendingUploads);
                    loaders.on('remove', checkPendingUploads);

                    setIsEditorReady(true);
                }}
                onChange={(event, editor) => {
                    if (editor.state === 'destroyed') return;
                    const html = editor.getData();
                    emittedRef.current = [html, ...emittedRef.current].slice(0, EMITTED_MEMORY);
                    if (onChangeRef.current) onChangeRef.current(html);
                }}
            />
        </>
    );
};

export default TextEditor;